import React, { useMemo } from 'react';
import {
  BubbleBadge,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
  BubbleTypeahead,
} from '../BubbleUI';
import { Resource } from '@go-shared/types/groupBooking';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandGroupResourceType } from '@go-shared/types/zod/brand';
import uuid from 'uuid';

interface AddResourceFormProps {
  resources: Resource[];
  selectedResources: Resource[];
  resourceTypes: BrandGroupResourceType[];
  onUpdate: (val: Resource) => void;
  onTrash: (val: Resource) => void;
  onCreate: (val: Resource) => void;
  onAdd: (val: Resource) => void;
}

export default function AddResourceForm(
  props: React.HTMLAttributes<HTMLDivElement> & AddResourceFormProps
) {
  const { resources, selectedResources, resourceTypes, onUpdate, onTrash, onCreate, onAdd } = props;

  const resourcesOpts = useMemo(
    () =>
      resources
        .filter(resource => !selectedResources.some(selected => selected._id === resource._id))
        .map(r => ({
          key: r._id,
          refKey: r._id,
          item: r,
        })),
    [resources, selectedResources]
  );

  function handleAmountChange(resource: Resource, newAmount: number) {
    const updatedResource = {
      ...resource,
      amount: newAmount,
    };
    onUpdate(updatedResource);
  }

  function handlePriceChange(resource: Resource, price: number) {
    const updatedResource = {
      ...resource,
      price,
    };
    onUpdate(updatedResource);
  }

  function handleAddResource(resource: Resource) {
    const updatedResource = {
      ...resource,
      amount: 1,
    };
    onAdd(updatedResource);
  }

  function handleResourceCreation(name: string) {
    const newResource = {
      _id: uuid(), // for react keys
      resourceType: resourceTypes[0].name,
      name,
      archived: false,
      amount: 0,
      price: 0,
      resourceId: '',
    };
    onCreate(newResource);
  }

  function handleResourceTypeChange(resource: Resource) {
    const updatedResource = {
      ...resource,
      resourceType: resource.resourceType,
    };
    onUpdate(updatedResource);
  }

  return (
    <BubbleFormGroup label="Resources" className="col-span-3">
      <BubbleTypeahead
        block
        options={resourcesOpts}
        placeholder="Enter rental name..."
        menuClassName="max-h-80 overflow-y-auto"
        allowNew
        noSuggestionsClick
        suggestionsText="No similar rental found"
        suggestionClassName="p-1-i"
        showSelected
        labelExtractor={r => r.name}
        onCreate={val => handleResourceCreation(val)}
        selectedValues={selectedResources.map(e => {
          return { key: e._id, refKey: e._id, item: e };
        })}
        selectedOptionsRenderFn={value => (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="p-1-i">
                {value.item.name} {`($${value.item.price})`}
              </span>
            </div>
            <div className="flex items-center">
              {value.item.resourceId && (
                <BubbleBadge
                  key={value.item.resourceType}
                  variant="navy"
                  className={`bt-badge b-tab mr-1 mb-1`}
                  inverse
                >
                  {value.item.resourceType}
                </BubbleBadge>
              )}
              {!value.item.resourceId && (
                <>
                  <BubbleSelect
                    className="pl-3 py-1"
                    appearance="bare"
                    allowUndefined={false}
                    options={resourceTypes.map(r => r.name)}
                    labelExtractor={type => type}
                    value={value.item.resourceType}
                    onValue={val => {
                      if (val) handleResourceTypeChange({ ...value.item, resourceType: val });
                    }}
                  />

                  <span className="pl-3 text-xs font-semibold uppercase text-navy">Price:</span>

                  <BubbleInput
                    type="number"
                    min="0"
                    step="0.1"
                    appearance="bare"
                    className="w-16"
                    value={value.item.price}
                    inputClassName="pl-3 py-1 rounded"
                    onValue={val => {
                      const price = val || 0;
                      handlePriceChange(value.item, price);
                    }}
                  />
                </>
              )}

              <span className="pl-2 text-xs font-semibold uppercase text-navy">Qty:</span>
              <BubbleInput
                type="number"
                min="1"
                appearance="bare"
                className="w-16"
                value={value.item.amount}
                inputClassName="pl-3 py-1 rounded"
                onValue={val => {
                  const newAmount = val || 1;
                  handleAmountChange(value.item, newAmount);
                }}
              />
              <div
                className="suggestions-action"
                tabIndex={-1}
                onMouseDown={() => onTrash(value.item)}
              >
                <FontAwesomeIcon icon="trash-alt" />
              </div>
            </div>
          </div>
        )}
        suggestionsRenderFn={(_, opt) => (
          <div
            className="flex items-center justify-between"
            onMouseDown={() => handleAddResource(opt.item)}
          >
            <span className="p-1-i">
              {opt.item.name} {`($${opt.item.price})`}
            </span>
            <span className="px-2 text-xs font-semibold uppercase text-navy">
              {opt.item.resourceType}
            </span>
          </div>
        )}
      />
    </BubbleFormGroup>
  );
}
